export * from "./marketTypes"
import { sports } from "../constants/sports"
import { getMarketTypeDetails } from "./marketTypes"

// Helper function to extract market hashes from active markets
export const extractMarketHashes = (activeMarkets: any): string[] => {
  if (!activeMarkets?.data?.markets) {
    return []
  }

  return activeMarkets.data.markets
    .map((market: any) => market.marketHash)
    .filter(Boolean)
}

// Helper function to get team names from activeMarkets based on marketHash
export const getTeamNamesFromMarketHash = (
  activeMarkets: any,
  marketHash: string
) => {
  if (!activeMarkets?.data?.markets) {
    return { teamOneName: "Unknown", teamTwoName: "Unknown" }
  }

  const market = activeMarkets.data.markets.find(
    (market: any) => market.marketHash === marketHash
  )

  if (!market) {
    return { teamOneName: "Unknown", teamTwoName: "Unknown" }
  }

  return {
    teamOneName: market.teamOneName || "Unknown",
    teamTwoName: market.teamTwoName || "Unknown",
  }
}

// Helper function to get market type details from activeMarkets based on marketHash
export const getMarketTypeFromMarketHash = (
  activeMarkets: any,
  marketHash: string
) => {
  if (!activeMarkets?.markets) {
    return getMarketTypeDetails(0) // Return unknown market type
  }

  const market = activeMarkets.markets.find(
    (market: any) => market.marketHash === marketHash
  )

  if (!market || !market.type) {
    return getMarketTypeDetails(0) // Return unknown market type
  }

  return getMarketTypeDetails(market.type)
}

export const calculateWeightedOdds = (amount: number, allOdds: any) => {
  // Sort odds by odd value and takerBetAmount in descending order
  const sortedOdds = [...allOdds].sort((a, b) => {
    return b.odd - a.odd
  })

  let remainingAmount = amount
  let weightedSum = 0
  let totalWeight = 0

  // Calculate weighted odds
  for (const odd of sortedOdds) {
    if (remainingAmount <= 0) {
      break
    }

    // Use the minimum of remaining amount and takerBetAmount
    const usedAmount = Math.min(remainingAmount, odd.takerBetAmount)
    weightedSum += usedAmount * odd.odd
    totalWeight += usedAmount
    remainingAmount -= usedAmount
  }

  // Return weighted odds or 0 if no weight was accumulated
  return totalWeight > 0 ? weightedSum / totalWeight : 0
}

export const getSportLabel = (id: number): string | undefined => {
  return sports.find(s => s.sportId === id)?.label
}

export const transformToRequiredObj = (currentObj: any) => {
  return {
    status: currentObj.periods.some(p => !p.isFinished)
      ? "In progress"
      : "Finished",

    scoreBoard: {
      points: {
        h: String(currentObj.teamOneScore * 5), // <-- arbitrary mapping, adjust logic
        g: String(currentObj.teamTwoScore * 3), // <-- arbitrary mapping, adjust logic
      },

      // map each inning to s1..s5
      s1: {
        h: Number(currentObj.periods[0]?.teamOneScore ?? -1),
        g: Number(currentObj.periods[0]?.teamTwoScore ?? -1),
      },
      s2: {
        h: Number(currentObj.periods[1]?.teamOneScore ?? -1),
        g: Number(currentObj.periods[1]?.teamTwoScore ?? -1),
      },
      s3: {
        h: Number(currentObj.periods[2]?.teamOneScore ?? -1),
        g: Number(currentObj.periods[2]?.teamTwoScore ?? -1),
      },
      s4: {
        h: Number(currentObj.periods[3]?.teamOneScore ?? -1),
        g: Number(currentObj.periods[3]?.teamTwoScore ?? -1),
      },
      s5: {
        h: Number(currentObj.periods[4]?.teamOneScore ?? -1),
        g: Number(currentObj.periods[4]?.teamTwoScore ?? -1),
      },

      sets: {
        h: currentObj.teamOneScore > currentObj.teamTwoScore ? 1 : 0,
        g: currentObj.teamTwoScore > currentObj.teamOneScore ? 1 : 0,
      },

      servis: {
        h: currentObj.extra?.Turn === "1" ? "true" : "false",
        g: currentObj.extra?.Turn === "2" ? "true" : "false",
      },

      state: `S${currentObj.currentPeriod?.split(" ")[0] ?? 1}`,
    },

    stats: {
      aces: { h: 4, g: 0 }, // placeholder, replace with actual mapping
      breakPoints: { h: -1, g: -1 },
      breakPointsConversion: { h: -1, g: -1 },
      winFirstServePerc: { h: -1, g: -1 },
      winSecondServePerc: { h: -1, g: -1 },
      pointsOnOwnServe: { h: -1, g: -1 },
      serviceFaults: { h: 13, g: 14 }, // placeholder
      doubleFaults: { h: -1, g: -1 },
      totalPointsWon: {
        h: currentObj.teamOneScore * 10, // sample logic
        g: currentObj.teamTwoScore * 5,
      },
    },

    virtualCourtId: "3e4289b3-834a-5a61-b404-f7992458831a", // static for now
    timeline: [],
    jerseyColors: {
      h: "EAFF00",
      g: "447437",
    },
    clock: null,
  }
}
